import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useProcessingStatsSync } from '@/hooks/useProcessingStatsSync';
import {
  canAccessManagement,
  MANAGEMENT_ROOT,
} from '@/lib/utils/managementAccess';
import { bootstrapSession } from '@/lib/utils/session';
import { useAgenticStore } from '@/store/agenticStore';
import { useSessionStore } from '@/store/sessionStore';

const Agentic = lazy(() =>
  import('../-agentic').then(m => ({ default: m.Agentic })),
);

export const Route = createFileRoute('/(client)')({
  beforeLoad: async () => {
    // The mirror of the management guard, and the same predicate read the other
    // way: a session that belongs in management has no business in the customer
    // app. Retraced staff acting as themselves have no company data of their own
    // to look at — impersonation is how they see a customer's, and that is
    // exactly the case this lets through.
    //
    // Off unless the environment serves management from v2, so an environment
    // that has not switched it on keeps landing staff here as it does today.
    const user = useSessionStore.getState().user ?? (await bootstrapSession());
    if (canAccessManagement(user)) throw redirect({ to: MANAGEMENT_ROOT });
  },
  component: ClientLayout,
});

function ClientLayout() {
  const agenticOpen = useAgenticStore(s => s.isOpen);
  const toggleAgentic = useAgenticStore(s => s.toggle);
  useProcessingStatsSync();

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'client-layout',
    storage: localStorage,
    panelIds: ['client-main', 'client-agentic'],
  });

  return (
    <ResizablePanelGroup
      orientation='horizontal'
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
      className='h-full'
    >
      <ResizablePanel
        id='client-main'
        defaultSize={agenticOpen ? 65 : 100}
        minSize={40}
        className='min-w-0 overflow-hidden'
      >
        <div className='@container h-full flex flex-col overflow-hidden'>
          <Outlet />
        </div>
      </ResizablePanel>

      {agenticOpen && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel
            id='client-agentic'
            defaultSize={35}
            minSize={20}
            className='overflow-hidden border-l border-border bg-background'
          >
            <Suspense fallback={null}>
              <Agentic onClose={toggleAgentic} />
            </Suspense>
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
}
